import { type ComponentInstance } from '@uniformdev/canvas';

export type FormFieldWidth = 'full' | 'half';
export type FormChoiceControlType = 'select' | 'radio' | 'checkboxGroup';
export type FormConsentMode =
	| 'none'
	| 'privacyPolicy'
	| 'generalMarketingOptIn'
	| 'termsOfService'
	| 'eapTermsOfService'
	| 'eapVirtualAgentTermsAndConditions';

type FormFieldBase = {
	id: string;
	type: string;
	width: FormFieldWidth;
};

export type FormNamesFields = FormFieldBase & {
	type: 'formNamesFields';
	required: boolean;
};

export type FormEmailField = FormFieldBase & {
	type: 'formEmailField';
	businessEmailOnly: boolean;
	label?: string;
	requiredMessage?: string;
};

export type FormCompanyField = FormFieldBase & {
	type: 'formCompanyField';
	required: boolean;
	label?: string;
	requiredMessage?: string;
};

export type FormPhoneNumberField = FormFieldBase & {
	type: 'formPhoneNumberField';
	required: boolean;
};

export type FormCustomTextField = FormFieldBase & {
	type: 'formCustomTextField';
	payloadKey: string;
	label: string;
	placeholder?: string;
	helpText?: string;
	required: boolean;
	requiredMessage?: string;
	multiline: boolean;
};

export type FormOption = {
	id: string;
	value: string;
	label: string;
	conditionalFields: FormField[];
};

export type FormCustomChoiceField = FormFieldBase & {
	type: 'formCustomChoiceField';
	controlType: FormChoiceControlType;
	payloadKey: string;
	label: string;
	placeholder?: string;
	helpText?: string;
	required: boolean;
	requiredMessage?: string;
	options: FormOption[];
};

export type FormField =
	| FormNamesFields
	| FormEmailField
	| FormCompanyField
	| FormPhoneNumberField
	| FormCustomTextField
	| FormCustomChoiceField;

type FlatParameters = Record<string, unknown>;

const asString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim() ? value.trim() : undefined;

const asBoolean = (value: unknown): boolean => value === true;

const getParameters = (component: ComponentInstance): FlatParameters =>
	Object.fromEntries(
		Object.entries(component.parameters ?? {}).map(([key, parameter]) => [key, parameter?.value]),
	);

const getComponents = (component: ComponentInstance, slotName: string): ComponentInstance[] =>
	(component.slots?.[slotName] ?? []) as ComponentInstance[];

const parseOption = (component: ComponentInstance): FormOption => {
	const parameters = getParameters(component);
	return {
		id: component._id ?? '',
		value: asString(parameters['value']) ?? '',
		label: asString(parameters['label']) ?? '',
		conditionalFields: parseFormFields(getComponents(component, 'conditionalFields')),
	};
};

const parseFormField = (component: ComponentInstance): FormField | null => {
	const parameters = getParameters(component);
	const base = {
		id: component._id ?? '',
		width: parameters['width'] === 'half' ? ('half' as const) : ('full' as const),
	};

	switch (component.type) {
		case 'formNamesFields':
			return { ...base, type: component.type, required: asBoolean(parameters['required']) };
		case 'formEmailField':
			return {
				...base,
				type: component.type,
				businessEmailOnly: asBoolean(parameters['businessEmailOnly']),
				label: asString(parameters['label']),
				requiredMessage: asString(parameters['requiredMessage']),
			};
		case 'formCompanyField':
			return {
				...base,
				type: component.type,
				required: asBoolean(parameters['required']),
				label: asString(parameters['label']),
				requiredMessage: asString(parameters['requiredMessage']),
			};
		case 'formPhoneNumberField':
			return { ...base, type: component.type, required: asBoolean(parameters['required']) };
		case 'formCustomTextField':
			return {
				...base,
				type: component.type,
				payloadKey: asString(parameters['payloadKey']) ?? '',
				label: asString(parameters['label']) ?? '',
				placeholder: asString(parameters['placeholder']),
				helpText: asString(parameters['helpText']),
				required: asBoolean(parameters['required']),
				requiredMessage: asString(parameters['requiredMessage']),
				multiline: asBoolean(parameters['multiline']),
			};
		case 'formCustomChoiceField':
			return {
				...base,
				type: component.type,
				controlType:
					parameters['controlType'] === 'radio' || parameters['controlType'] === 'checkboxGroup'
						? parameters['controlType']
						: 'select',
				payloadKey: asString(parameters['payloadKey']) ?? '',
				label: asString(parameters['label']) ?? '',
				placeholder: asString(parameters['placeholder']),
				helpText: asString(parameters['helpText']),
				required: asBoolean(parameters['required']),
				requiredMessage: asString(parameters['requiredMessage']),
				options: getComponents(component, 'options').map(parseOption),
			};
		default:
			return null;
	}
};

export const parseFormFields = (components: ComponentInstance[]): FormField[] =>
	components.map(parseFormField).filter((field): field is FormField => field !== null);

export const getFormFields = (component: ComponentInstance): FormField[] =>
	parseFormFields(getComponents(component, 'fields'));

export const getFieldPayloadKeys = (field: FormField): string[] => {
	switch (field.type) {
		case 'formNamesFields':
			return ['firstName', 'lastName'];
		case 'formEmailField':
			return ['email'];
		case 'formCompanyField':
			return ['company'];
		case 'formPhoneNumberField':
			return ['number'];
		case 'formCustomTextField':
		case 'formCustomChoiceField':
			return field.payloadKey ? [field.payloadKey] : [];
	}
};
